import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import React, { useState, useEffect, Fragment } from 'react';

import { Grid, Box, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { snackAlertData } from 'utils/constants';
import { integrationModalStyles } from '../style/style';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { useAutomatiqIndyService } from 'store/services/automatiqIndy.service';
import { Modal, TextField, SnackBarAlert, CircleLoading, Button } from 'shared';

const AutomatiqIndyModal = ({ open, onClose, onSuccess, isConnected }) => {
    const classes = integrationModalStyles();
    const companyId = useSelector((state) => state?.company?.data?.id);
    const { addToken, getToken, updateToken } = useAutomatiqIndyService();

    const [userId, setUserId] = useState('');
    const [apiToken, setApiToken] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [buttonMode, setButtonMode] = useState('add');
    const [originalToken, setOriginalToken] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [organizationId, setOrganizationId] = useState('');
    const [buttonLoading, setButtonLoading] = useState(false);
    const [alertData, setAlertData] = useState(snackAlertData);

    const fetchToken = async () => {
        try {
            const response = await getToken({ companyId });
            const { start_date, api_token, organization_id, user_id } = response?.data;
            if (api_token) {
                setUserId(user_id);
                setApiToken(api_token);
                setOriginalToken(api_token);
                setOrganizationId(organization_id);
                setSelectedDate(dayjs(start_date));
                setButtonMode('update');
            }
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setButtonLoading(true);

            const data = {
                company_id: companyId,
                apiToken: apiToken,
                startDate: selectedDate.format('YYYY-MM-DD'),
                userId,
                organizationId,
            };

            const response = buttonMode === 'add' ? await addToken(data) : await updateToken(data);

            onSuccess(response?.message);
        } catch (error) {
            handleErrorAlert(setAlertData, undefined, error);
        } finally {
            setButtonLoading(false);
        }
    };

    useEffect(() => {
        if (companyId && isConnected) {
            fetchToken();
        }
    }, [companyId]);

    return (
        <Modal
            fullWidth
            open={open}
            title={`${buttonMode === 'update' ? 'Update' : 'Add'} Indy Token`}
            handleClose={() => {
                onClose();
            }}
            actionBtnTitle={<Typography>{buttonMode === 'update' ? 'Update' : 'Add'}</Typography>}
        >
            <Fragment>
                {isLoading ? (
                    <CircleLoading />
                ) : (
                    <>
                        {buttonMode === 'update' && originalToken != apiToken && (
                            <Typography variant="body2" color="error" marginBottom={2}>
                                Warning: Updating your Indy token will prevent fetching data from
                                your previously connected token.
                            </Typography>
                        )}

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6}>
                                <TextField
                                    inlineLabel="API Token"
                                    value={apiToken || ''}
                                    onChange={(e) => setApiToken(e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    placeholder="Enter your Indy API Token"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    inlineLabel="User Id"
                                    value={userId || ''}
                                    onChange={(e) => setUserId(e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    placeholder="Enter your User Id"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    inlineLabel="Organization Id"
                                    value={organizationId || ''}
                                    onChange={(e) => setOrganizationId(e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    placeholder="Enter your Organization Id"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        sx={classes.datePicker}
                                        label="Select Start Date"
                                        value={selectedDate}
                                        onChange={(newValue) => setSelectedDate(newValue)}
                                        disabled={
                                            buttonMode === 'update' && apiToken === originalToken
                                        }
                                        renderInput={(params) => (
                                            <TextField {...params} fullWidth sx={{ mb: 3 }} />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>

                        <Box mt={2} display="flex" justifyContent="flex-end">
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                loading={buttonLoading}
                                disabled={!apiToken || !selectedDate || originalToken == apiToken}
                                text={buttonMode === 'update' ? 'Update Token' : 'Add Token'}
                            />
                        </Box>
                    </>
                )}

                <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
            </Fragment>
        </Modal>
    );
};

export default AutomatiqIndyModal;
